import { QueryKeys } from "@/constants";
import { useMutation, useQuery } from "@tanstack/react-query";
import {
  fetchPaperlessEob,
  updatePaperlessEob,
} from "../services/paperlessEob";
import type {
  PaperlessEobFetchRequest,
  PaperlessEobFetchResponse,
} from "@/model/paperlessEob";
import { PAPARLESS_EOB_UPDATE_ACTION } from "@/model/paperlessEob";
import useUserInfo from "@/hooks/use-user-info";

export const usePaperlessEob = (memberId: string) => {
  const userInfo = useUserInfo();

  const fetchReqData: PaperlessEobFetchRequest = {
    groupId: userInfo?.groupId || "",
    memberId: memberId || "",
    accessToken: userInfo?.accessToken || "",
  };

  const fetchPaperlessEobQuery = useQuery<PaperlessEobFetchResponse>({
    queryKey: [QueryKeys.PAPERLESS_EOB_FETCH],
    queryFn: () => fetchPaperlessEob(fetchReqData),
    retry: false,
    retryOnMount: false,
    staleTime: Infinity,
    enabled: Boolean(memberId) && Boolean(fetchReqData?.accessToken),
  });

  const goPaperlessMutation = useMutation({
    mutationKey: [
      QueryKeys.PAPERLESS_EOB_UPDATE,
      PAPARLESS_EOB_UPDATE_ACTION.PAPERLESS,
    ],
    mutationFn: updatePaperlessEob,
  });

  const dontAskAgainMutation = useMutation({
    mutationKey: [
      QueryKeys.PAPERLESS_EOB_UPDATE,
      PAPARLESS_EOB_UPDATE_ACTION.DONT_ASK_AGAIN,
    ],
    mutationFn: updatePaperlessEob,
  });

  return {
    // Query results
    paperlessEobStatus: fetchPaperlessEobQuery.data,
    isLoadingStatus: fetchPaperlessEobQuery.isLoading,

    // Mutation actions
    updatePaperlessEob: goPaperlessMutation.mutate,
    dontAskAgainPaperlessEob: dontAskAgainMutation.mutate,

    // Mutation states
    isGoPaperlessUpdating: goPaperlessMutation.isPending,
    isUpdateSuccess: goPaperlessMutation.isSuccess,
    isUpdateError: goPaperlessMutation.isError,
    updateError: goPaperlessMutation.error,

    isDontAskAgainUpdating: dontAskAgainMutation.isPending,
  };
};
